import { Controller } from "@hotwired/stimulus"

// Manages file attachment previews in the chat message form.
// Shows a strip of thumbnails/filenames before sending.
export default class extends Controller {
  static targets = ["fileInput", "previews"]

  paste(event) {
    const items = event.clipboardData?.items
    if (!items) return

    const imageFiles = []
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile()
        if (file) imageFiles.push(file)
      }
    }
    if (!imageFiles.length) return

    event.preventDefault()

    // Append to existing files via DataTransfer
    const dt = new DataTransfer()
    Array.from(this.fileInputTarget.files).forEach(f => dt.items.add(f))
    imageFiles.forEach(f => dt.items.add(f))
    this.fileInputTarget.files = dt.files

    this.filesChosen()
    this.fileInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  openPicker() {
    this.fileInputTarget.click()
  }

  filesChosen() {
    const files = Array.from(this.fileInputTarget.files)
    if (!files.length) return

    this.previewsTarget.innerHTML = ""
    this.previewsTarget.classList.remove("hidden")
    this.previewsTarget.classList.add("flex")

    files.forEach((file, index) => {
      const preview = this.buildPreview(file, index)
      this.previewsTarget.appendChild(preview)
    })
  }

  buildPreview(file, index) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative group"
    wrapper.dataset.index = index

    if (file.type.startsWith("image/")) {
      const img = document.createElement("img")
      img.className = "size-14 rounded-lg object-cover border border-slate-200"
      img.alt = file.name
      const reader = new FileReader()
      reader.onload = (e) => { img.src = e.target.result }
      reader.readAsDataURL(file)
      wrapper.appendChild(img)
    } else {
      const pill = document.createElement("div")
      pill.className = "flex items-center gap-1.5 bg-slate-100 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 max-w-[120px]"
      pill.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
        </svg>
        <span class="truncate">${file.name}</span>
      `
      wrapper.appendChild(pill)
    }

    // Remove button
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "absolute -top-1.5 -right-1.5 size-5 bg-slate-600 text-white rounded-full flex items-center justify-center text-xs leading-none hidden group-hover:flex active:bg-slate-800"
    removeBtn.innerHTML = "×"
    removeBtn.addEventListener("click", () => this.removeFile(index))
    wrapper.appendChild(removeBtn)

    return wrapper
  }

  removeFile(removeIndex) {
    // Rebuild a new FileList without the removed file
    const dt = new DataTransfer()
    Array.from(this.fileInputTarget.files)
      .filter((_, i) => i !== removeIndex)
      .forEach(f => dt.items.add(f))

    this.fileInputTarget.files = dt.files

    if (dt.files.length === 0) {
      this.clearPreviews()
    } else {
      this.filesChosen()
    }
    // Notify chat controller to recheck sendability
    this.fileInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  clearPreviews() {
    this.previewsTarget.innerHTML = ""
    this.previewsTarget.classList.add("hidden")
    this.previewsTarget.classList.remove("flex")
    this.fileInputTarget.value = ""
  }
}
